import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, Calendar, Archive, CheckCheck } from 'lucide-react';
import { useHistory } from "react-router-dom";
import notificationService from "../services/notificationService";

const POLL_INTERVAL_MS = 30000;

const formatTime = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return "Az önce";
  if (diffMin < 60) return `${diffMin} dk önce`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)} sa önce`;
  return date.toLocaleDateString("tr-TR");
};

export default function NotificationBell() {
  const history = useHistory();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const containerRef = useRef();

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationService.getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      // Zil hatası sayfayı bozmasın; bir sonraki denemede tekrar çekilir
      console.error("Bildirimler alınamadı:", err);
    }
  }, []);

  // Periyodik olarak yeni bildirimleri kontrol et
  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  // Dışarı tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const handleClick = async (notification) => {
    if (!notification.is_read) {
      setNotifications(prev => prev.map(n => n._id === notification._id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(prev - 1, 0));
      notificationService.markRead(notification._id).catch(err => console.error(err));
    }
    setOpen(false);
    if (notification.link) history.push(notification.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      <button
        onClick={toggleOpen}
        aria-label={`Bildirimler${unreadCount ? ` (${unreadCount} okunmamış)` : ""}`}
        style={{
          position: "relative",
          background: "#f8f9fa",
          border: "1px solid #e0e0e0",
          borderRadius: "50%",
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#333",
        }}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 9,
              background: "#e53935",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="notification-dropdown"
          style={{
            position: "absolute",
            top: 48,
            right: 0,
            width: 340,
            maxHeight: 420,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            zIndex: 1002,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderBottom: "1px solid #f0f0f0",
              position: "sticky",
              top: 0,
              background: "#fff",
            }}
          >
            <strong style={{ fontSize: 15, color: "#333" }}>Bildirimler</strong>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: "none",
                  border: "none",
                  color: "#42a947",
                  cursor: "pointer",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <CheckCheck size={14} /> Tümünü okundu yap
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div style={{ padding: "24px 16px", textAlign: "center", color: "#888", fontSize: 14 }}>
              Henüz bildiriminiz yok
            </div>
          ) : (
            notifications.map(notification => (
              <div
                key={notification._id}
                onClick={() => handleClick(notification)}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "12px 16px",
                  borderBottom: "1px solid #f5f5f5",
                  cursor: "pointer",
                  background: notification.is_read ? "#fff" : "#f1f8f1",
                }}
              >
                <div style={{ color: "#42a947", paddingTop: 2 }}>
                  {notification.category === "borrow" ? <Archive size={18} /> : <Calendar size={18} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: notification.is_read ? 500 : 700, color: "#333" }}>
                    {notification.title}
                  </div>
                  <div style={{ fontSize: 13, color: "#555", marginTop: 2, wordBreak: "break-word" }}>
                    {notification.message}
                  </div>
                  <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                    {formatTime(notification.created_at)}
                  </div>
                </div>
                {!notification.is_read && (
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#e53935", marginTop: 6 }} />
                )}
              </div>
            ))
          )}
        </div>
      )}
      <style>{`
        @media (max-width: 480px) {
          .notification-dropdown {
            position: fixed !important;
            top: 64px !important;
            left: 8px !important;
            right: 8px !important;
            width: auto !important;
          }
        }
      `}</style>
    </div>
  );
}
